import {
  AfterViewInit,
  Component,
  inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { AlertService } from '@modules/alert/services/alert.service';
import { AlertButtonConfig } from '@modules/alert/types/config';
import { BaseAppStateService } from '@modules/common/services/base-app-state.service';
import { BaseStitchService } from '@modules/common/services/base-stitch.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { StitchEventsService } from '@modules/common/services/stitch-events-service';
import { StitchService } from '@modules/common/services/stitch.service';
import { Application } from '@modules/common/types/application';
import { Stitch } from '@modules/common/types/stitch';
import { StitchFilters } from '@modules/common/types/stitch-filters';
import { EntityTypeToStitchType } from '@modules/common/utils/stitch';
import { Connection } from '@modules/connections/types/connection';
import { SelectableListDirective } from '@modules/drag-n-drop/directives/selectable-list.directive';
import { DragData, DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import { type SelectableItem } from '@modules/drag-n-drop/types/selectable-item';
import { Knot } from '@modules/knots/types/knot';
import { ModalService } from '@modules/modal/services/modal.service';
import { PopoverService } from '@frontendlib/components/features/popover/services/popover.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { StateKey } from '@modules/settings/types/state-key';
import { Team } from '@modules/settings/types/team';
import { Tag } from '@modules/tags/types/tag';
import { SignalStitchEventType } from '@sharedlib/common/types/signal-type';
import { structHash } from '@sharedlib/common/utils/struct-hash';
import { StitchItemSummaryDto } from '@sharedlib/dtos/dto/StitchItemSummaryDto';
import { BehaviorSubject, combineLatest, merge, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  template: '',
  standalone: false,
})
export abstract class BaseSidebarContainersTreeComponent<
    C extends Stitch,
    I extends Stitch,
    Filters extends StitchFilters<StitchItemSummaryDto>,
    VirtualFolder extends string,
  >
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @Input() filters: Filters;
  @Input() team: Team;
  @Input() itemCreateFormTemplate: TemplateRef<void>;
  @Input() placeholder?: TemplateRef<any>;

  protected readonly selectableList = viewChild(SelectableListDirective);

  public items: C[] = [];
  public selectedItemsIds: string[] = [];
  public abstract application: Application;
  public abstract treeStateKey: StateKey;
  public abstract dndPredicate: (stitchItem: Stitch) => (dragData: DragData) => boolean;

  protected abstract selfDragDataTypes: DragDataTypes[];
  /**
   * The drag type for this tree's own container entity (e.g. project, notebook,
   * messageFolder). Used to short-circuit container-on-container drops directly to
   * Stitch — Copy and Move are not meaningful for those: the backend duplicate ignores
   * the drop target for container types, and re-parenting via Move duplicates the Move
   * To context-menu path. Stitching is the only useful action.
   */
  protected abstract containerDragDataType: DragDataTypes;
  protected alive = new Subject<void>();
  private selectedSynced = false;
  protected readonly injector: Injector = inject(Injector);
  protected readonly popoverService: PopoverService = inject(PopoverService);
  public readonly quickFormPopoverClose = new Subject<void>();

  private readonly filtersChanged = new BehaviorSubject<void>(null);
  private readonly stitchService: StitchService = inject(StitchService);
  private readonly searchService: AdvancedSearchService = inject(AdvancedSearchService);
  private readonly alertService: AlertService = inject(AlertService);
  private readonly modalService: ModalService = inject(ModalService);
  protected readonly stateService: BaseAppStateService<C, I, VirtualFolder> = inject(BaseAppStateService);

  private readonly stitchEventsService = inject(StitchEventsService);
  // `protected`, not `private`: a subclass that needs the space context must REUSE this one.
  // Re-declaring it in a subclass is a TS2415 that `vitest` and `nx typecheck` both pass and only
  // the production build rejects — it cost #1060 a merge-and-revert on chat-dev.
  protected readonly spaceContextStore = inject(SpaceContextStore);

  constructor(protected itemsService: BaseStitchService<EntityTypeToStitchType<C>, StitchItemSummaryDto, C, Filters>) {}

  /**
   * Lifecycle
   */

  ngOnInit() {
    this.stitchEventsService
      .getEvents(this.spaceContextStore.contextId(), this.itemsService.stitchType, SignalStitchEventType.UPDATED)
      .pipe(takeUntil(this.alive))
      .subscribe((payload) => {
        const ids = new Set(payload.ids);
        for (const item of this.items) {
          if (ids.has(item.id)) {
            Object.assign(item, payload.changes);
          }
        }
      });
  }

  public ngAfterViewInit(): void {
    this.selectableList() &&
      this.selectableList()
        .selectableService.getSelectedItems()
        .pipe(
          filter(() => this.selectedSynced),
          takeUntil(this.alive),
        )
        .subscribe((selectedItems: SelectableItem[]) => {
          // Only a tree the user acted IN names the tenant. This subscription fires on every emission
          // of the selectable service, and an empty one is never a user action here: it is this tree
          // reconciling its own selection away at :177-179 because the shared `containersIds` moved to
          // another tree. Without the guard the DEPARTING tree wrote ITS mailbox as the app tenant —
          // clicking Team Inbox with a member folder selected landed the app back on the member
          // mailbox, measured live 3/3 both directions (#1065 r2 ROW 1, review finding 1).
          if (selectedItems.length > 0) {
            this.beforeContainerSelectionApplied();
          }
          const state = this.searchService.getStateSync();
          const containersIds = selectedItems.map((item) => item.identifier);
          state[this.application].containersIds = containersIds;
          const name = this.resolveContainerName(containersIds);
          if (name !== undefined) {
            state[this.application].containerName = name;
          }
          this.searchService.setState(state);
        });

    combineLatest([
      this.getSelectedItemUids(),
      merge(
        this.filtersChanged.pipe(
          filter(() => !!this.filters),
          map(() => structHash(this.filters)),
          distinctUntilChanged(),
        ),
        this.itemsService.getRefresh().pipe(filter(() => !!this.filters)),
      ).pipe(
        switchMap(() =>
          this.itemsService.findAll(
            {
              ...this.filters,
              deleted: false,
              archived: false,
              snoozedOnTop: false,
              followedOnTop: false,
            },
            this.requestContext() ? { requestContext: this.requestContext() } : undefined,
          ),
        ),
      ),
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([selectedItemsIds, items]) => {
        if (this.selectedSynced) {
          this.selectedItemsIds = selectedItemsIds;
        } else {
          this.selectedItemsIds = items.reduce((arr, item) => {
            if (selectedItemsIds.includes(item.id)) {
              arr.push(item.id);
            }
            return arr;
          }, []);

          this.selectedSynced = true;
        }

        if (this.selectableList()) {
          const selectedItems = this.selectableList().selectableService.getSelectedItemsSync();
          const newSelected = selectedItems.filter((item) => this.selectedItemsIds.includes(item.identifier));
          if (newSelected.length !== selectedItems.length) {
            this.selectableList().selectableService.setSelectedItems(newSelected);
          }
        }

        const itemsChanged = items !== this.items;
        this.items = items;
        // Correct the mirrored name once items load (a container may have been opened before its title resolved).
        if (itemsChanged) {
          this.refreshContainerName();
        }
        this.onItemsReceived();
      });
  }

  protected getSelectedItemUids(): Observable<string[]> {
    return this.searchService.getState().pipe(
      map(({ [this.application]: { containersIds } }) => containersIds),
      takeUntil(this.alive),
    );
  }

  /** Optional per-request scope for trees that render data outside the currently selected tenant. */
  protected requestContext(): HttpContext | undefined {
    return undefined;
  }

  /**
   * Runs immediately before a selection change is written into `AdvancedSearchService`.
   *
   * That write happens on the NATIVE click — `SelectableItemDirective` emits synchronously — while
   * `openItem`, which is where a tree names its tenant, sits behind `appClick`'s `timer(200)`. So the
   * `containersIds` write, and the list read it triggers through both list-container tuples, used to
   * leave under the tenant the user was leaving: a cross-mailbox folder click issued a request under
   * the previous mailbox before any of the correct ones (#1049 ROW 3, #1065 ROW 5).
   *
   * Deliberately empty and tenant-free here — each tree knows whether it owns a tenant selection.
   * Mail and Notes mount two tenant-specific trees at once and override this hook before their native
   * click selection write can rebuild a list under the tenant being left.
   */
  protected beforeContainerSelectionApplied(): void {}

  /**
   * Drops a persisted container selection none of this tree's rows can satisfy.
   *
   * The selection lives in `AdvancedSearchService` under `LSItem({ lsKey: 'as' })` with the panel as
   * its only variant, so it is keyed by panel and never by space or tenant and outlives every tenant
   * change: clearing one tenant key while a folder of the OTHER tenant stayed selected paired
   * `space_id=<team>` with `folders_ids[]=<member folder>` and rendered an empty list with no pager,
   * silently (#1065 ROW 6, measured on the public site).
   *
   * Neutral by design: the base cannot know which mounted tree owns the app's current tenant, so the
   * subclass decides WHEN to call this and this method only decides WHETHER the ids still resolve.
   */
  protected dropForeignContainerSelection(): void {
    const state = this.searchService.getStateSync();
    const containersIds = state[this.application].containersIds;
    if (containersIds.length === 0) {
      return;
    }
    if (containersIds.some((id) => this.items.some((item) => item.id === id))) {
      return;
    }
    state[this.application].containersIds = [];
    state[this.application].containerName = null;
    this.searchService.setState(state);
  }

  // Resolves the opened container's display name from the current selection.
  // null → nothing open; undefined → the container isn't in `items` yet, so the
  // caller keeps the persisted name (avoids a blank header on the initial sync).
  private resolveContainerName(containersIds: string[]): string | null | undefined {
    const openedId = containersIds[0];
    if (!openedId) {
      return null;
    }
    const item = this.items.find((entry) => entry.id === openedId);
    return item ? item.title || 'Untitled' : undefined;
  }

  private refreshContainerName(): void {
    const state = this.searchService.getStateSync();
    const name = this.resolveContainerName(state[this.application].containersIds);
    if (name !== undefined && name !== state[this.application].containerName) {
      state[this.application].containerName = name;
      this.searchService.setState(state);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('filters' in changes) {
      this.filtersChanged.next();
    }
  }

  ngOnDestroy() {
    this.alive.next();
    this.alive.complete();
    this.quickFormPopoverClose.next();
    this.quickFormPopoverClose.complete();
  }

  /**
   * Actions
   */

  protected onItemsReceived(): void {
    // do nothing
  }

  abstract handleDuplicate(dragData: DragData, item: Stitch);

  abstract handleMove(dragData: DragData, item: Stitch);

  public handleMenuOpen(item: C): void {
    this.selectableList()?.selectableService.setSelectedItems([{ identifier: item.id, data: item }]);
    this.openItem(item);
  }

  openItem(item: C) {
    this.stateService.setMainView(item);
  }

  handleDblClick(item: C) {
    const requestContext = this.requestContext();
    if (requestContext) {
      this.modalService.openFullForm(item, this.injector, requestContext);
      return;
    }
    this.modalService.openFullForm(item, this.injector);
  }

  public handleStitch(dragData: DragData, item: Stitch) {
    this.stitchService.linkDragData(item, dragData);
  }

  protected getDndModalButtons(dragData: DragData, item: Stitch): AlertButtonConfig[] {
    return [
      {
        title: 'Cancel',
        close: true,
      },
      {
        title: 'Copy',
        click: () => this.handleDuplicate(dragData, item),
        close: true,
      },
      {
        title: 'Move',
        click: () => {
          this.handleMove(dragData, item);
        },
        close: true,
      },
      {
        title: 'Stitch',
        click: () => {
          this.handleStitch(dragData, item);
        },
        close: true,
      },
    ];
  }

  public dndDrop(dragData: DragData, item: Stitch) {
    // Container-on-container drops stitch immediately, with no modal — see
    // containerDragDataType for the rationale.
    if (dragData.type === this.containerDragDataType) {
      this.handleStitch(dragData, item);
      return;
    }
    if (this.selfDragDataTypes.includes(dragData.type)) {
      this.alertService.show({
        title: 'Copy, Move or Stitch?',
        body: 'What do we do with the dropped item?',
        rightButtons: this.getDndModalButtons(dragData, item),
      });
    } else {
      this.handleStitch(dragData, item);
    }
  }

  protected getIds(items: (StitchItemSummaryDto | Tag | Knot | Connection)[]): string[] {
    return items.map(({ id }) => id);
  }
}
